import { useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import { parseRunIdFromPathname } from 'Pods/ThreeDStudio/Hooks/parseRunIdFromPathname'
import { useThreeDStudioStore, threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  setHasGenerated,
  setHasFailed,
  setIsGenerating,
  setEnhancingSessionId,
  setRunId,
  setLastGenerationSettings,
  setAvailableCredits,
  setErrorDetails,
} from 'Pods/ThreeDStudio/Store/actions/generation'
import { applySubscriptionQuota } from 'Pods/ThreeDStudio/Store/actions/subscription'
import { setOpenedModel, openVariationsTab } from 'Pods/ThreeDStudio/Store/actions/ui'

export default function useGeneratorRunChannel() {
  const runId = useThreeDStudioStore(state => state.generation.runId)
  const subscriptionRef = useRef(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  // Capture latest navigate/location via refs so the channel subscription
  // does not re-create itself when these identities change between renders.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const locationRef = useRef(location)
  locationRef.current = location

  useEffect(() => {
    if (!runId || !window.App?.cable?.subscriptions) {
      return
    }

    function cleanup() {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
      setRunId(undefined)
      setLastGenerationSettings({})
    }

    subscriptionRef.current = window.App.cable.subscriptions.create(
      { channel: 'GeneratorRunsChannel', run_id: runId },
      {
        connected() {},
        rejected() {
          if (typeof window !== 'undefined' && window.showNotification) {
            window.showNotification('Connection failed. Please try again.', { type: 'error', autohide: false })
          }
          setIsGenerating(false)
          cleanup()
        },
        received(message) {
          if (message?.type !== 'generator/runs/update') {
            return
          }
          setIsGenerating(false)
          // The enhancement run is done; clear the session so its "Processing..."
          // card no longer shows.
          setEnhancingSessionId(undefined)
          cleanup()

          const attributes = message?.data?.attributes || {}
          const {
            outputAssets,
            createdAt,
            remainingCredits,
            threeDStudioQuota,
            failureReason,
            failureMessage,
            providerErrorCode,
            providerModelId,
            generatorSessionId,
          } = attributes
          setAvailableCredits(remainingCredits?.total_credits)
          applySubscriptionQuota(threeDStudioQuota)

          const fileUrl = outputAssets?.[0]?.fileUrl
          if (!fileUrl) {
            setErrorDetails({ failureReason, failureMessage, providerErrorCode })
            setHasFailed(true)
            return
          }

          setErrorDetails({})


          const openedModel = { ...attributes, createdAt: createdAt || new Date().toISOString() }

          // URL-is-source-of-truth: completion may hydrate the store only
          // when the URL already points at this run, or after replacing /ai
          // with this run's URL. Gallery and other model URLs are left alone;
          // the library refetch below will surface the completed run there.
          const currentPathname = locationRef.current?.pathname
          const completedRunId = attributes?.id
          const completedRunIdKey = completedRunId == null ? null : String(completedRunId)
          const urlRunId = parseRunIdFromPathname(currentPathname)
          const urlRunIdKey = urlRunId == null ? null : String(urlRunId)
          const completedRunPathname = buildSharedRunPathname({
            slug: attributes.slug,
            id: completedRunId,
            title: attributes.title ?? attributes.prompt,
          })

          const isEnhancementRun = !!VARIATION_TYPES[providerModelId]?.isEnhancement
          const isSegmentationRun = !!VARIATION_TYPES[providerModelId]?.isSegmentation
          const openedSessionId = threeDStudioStore.getState().ui?.openedModel?.generatorSessionId
          const isEnhancementOfOpenSession = isEnhancementRun
            && generatorSessionId != null
            && String(generatorSessionId) === String(openedSessionId)

          if (isEnhancementOfOpenSession && isSegmentationRun) {
            // Segmentation results stay in the Segment tab instead of jumping to
            // Variations. Hydrate the viewer from the broadcast payload (the
            // URL-sync refetch can miss the freshly-created private run) and align
            // the URL without forcing a tab switch.
            setOpenedModel(openedModel)
            navigateRef.current(completedRunPathname, { replace: true })
          } else if (isEnhancementOfOpenSession) {
            // A remesh/retexture adds a new variation to the open session rather
            // than a new library item. Open the Variations panel and select the
            // new variation: hydrate the viewer straight from the broadcast payload
            // (the URL-sync refetch can miss the freshly-created private run) so the
            // viewport shows the new variation, then align the URL.
            openVariationsTab()
            setOpenedModel(openedModel, { keepVariationsTab: true })
            navigateRef.current(completedRunPathname, { replace: true, state: { keepVariationsTab: true } })
          } else if (completedRunIdKey && currentPathname === '/ai') {
            navigateRef.current(completedRunPathname, { replace: true })
            setOpenedModel(openedModel)
          } else if (completedRunIdKey && urlRunIdKey === completedRunIdKey) {
            setOpenedModel(openedModel)
          }

          queryClient.invalidateQueries({ queryKey: ['variations'] })
          queryClient.refetchQueries({ queryKey: ['libraryItems', 'sessions'], exact: false })
          setHasGenerated(true)
        },
      }
    )

    return () => {
      cleanup()
    }
  }, [runId, queryClient])
}
